/**
 * Identity-key crypto. Every user has one long-term **Ed25519 identity keypair**
 * used to SIGN the encryption public keys they publish (their X25519 account key,
 * and — later phases — family/recovery keys), so a malicious server cannot
 * substitute a public key without breaking a signature (Public Key
 * Authentication; see docs/security/2026-08-22-malicious-server-threat-model-review.md
 * and docs/superpowers/specs/2026-08-22-public-key-authentication-design.md).
 *
 * Storage mirrors the X25519 account key (accountKeyCrypto.ts) exactly: the
 * private key is wrapped on the server under HKDF(exportKey,
 * "identity-signing-key-wrapping") and locally under the PBKDF2 local wrapping
 * key, both AES-256-GCM. Only the wrapping key differs between the two envelopes.
 *
 * Ed25519 signing/verifying lives in signedKey.ts; this module is keygen +
 * wrapping only. Ed25519 keygen uses @noble/curves (decision: one JS impl on
 * web + extension; the wrapping stays on WebCrypto like the account key).
 */
import { ed25519 } from '@noble/curves/ed25519.js';
import { bytesToBase64, base64ToBytes } from './encoding';

const IDENTITY_HKDF_INFO = 'identity-signing-key-wrapping';
const IV_LENGTH = 12;

/**
 * Generate a per-user Ed25519 identity keypair. Returns the raw 32-byte public
 * key and the 32-byte private seed. Minted once at registration and stored
 * twice-wrapped (server + local), exactly like the account key.
 */
export function generateIdentityKeyPair(): { publicKey: Uint8Array; privateKey: Uint8Array } {
  const privateKey = ed25519.utils.randomSecretKey();
  const publicKey = ed25519.getPublicKey(privateKey);
  return { publicKey, privateKey };
}

/**
 * Server-side wrapping key for the identity private key:
 * HKDF-SHA-256(exportKey, salt=32 zero bytes, info="identity-signing-key-wrapping").
 * Distinct info label from the account/vault wraps → fully domain-separated.
 */
export async function deriveIdentityWrappingKey(exportKey: string): Promise<CryptoKey> {
  const normalized = exportKey.replace(/-/g, '+').replace(/_/g, '/');
  const keyBytes = base64ToBytes(normalized);
  const keyMaterial = await crypto.subtle.importKey('raw', keyBytes as BufferSource, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(32),
      info: new TextEncoder().encode(IDENTITY_HKDF_INFO),
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function wrapIdentityPrivateKey(
  privateKey: Uint8Array,
  wrappingKey: CryptoKey,
): Promise<{ ciphertext: string; iv: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH) as Uint8Array<ArrayBuffer>);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrappingKey, privateKey as BufferSource),
  );
  return { ciphertext: bytesToBase64(ciphertext), iv: bytesToBase64(iv) };
}

export async function unwrapIdentityPrivateKey(
  ciphertext: string,
  iv: string,
  wrappingKey: CryptoKey,
): Promise<Uint8Array> {
  const ctBytes = base64ToBytes(ciphertext);
  const ivBytes = base64ToBytes(iv);
  return new Uint8Array(
    await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes.buffer as ArrayBuffer },
      wrappingKey,
      ctBytes.buffer as ArrayBuffer,
    ),
  );
}

export async function localWrapIdentityPrivateKey(
  privateKey: Uint8Array,
  localWrappingKey: CryptoKey,
): Promise<{ ciphertext: string; iv: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH) as Uint8Array<ArrayBuffer>);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, localWrappingKey, privateKey as BufferSource),
  );
  return { ciphertext: bytesToBase64(ciphertext), iv: bytesToBase64(iv) };
}

export async function localUnwrapIdentityPrivateKey(
  ciphertext: string,
  iv: string,
  localWrappingKey: CryptoKey,
): Promise<Uint8Array> {
  const ctBytes = base64ToBytes(ciphertext);
  const ivBytes = base64ToBytes(iv);
  return new Uint8Array(
    await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes.buffer as ArrayBuffer },
      localWrappingKey,
      ctBytes.buffer as ArrayBuffer,
    ),
  );
}
